<template>
  <v-container fluid class="fill-height login-container">
    <v-row justify="center" align="center" class="fill-height">
      <v-col cols="12" sm="10" md="11" lg="11">
        <v-card class="auth-shell overflow-hidden">
          <v-window v-model="step" touchless>
            <!-- LOGIN -->
            <v-window-item :value="1">
              <v-row no-gutters>
                <v-col cols="12" md="6" class="pa-8">
                  <div class="auth-section-header justify-center">
                    <v-icon small color="primary" class="mr-2">mdi-login-variant</v-icon>
                    <span class="auth-section-title">Acesso</span>
                  </div>

                  <div class="text-center mb-6">
                    <h2 class="auth-title mb-2">Bem-vindo</h2>
                    <p class="auth-subtitle">
                      Entre em sua conta para continuar
                    </p>
                  </div>

                  <v-form
                    ref="loginForm"
                    v-model="validForm"
                    @submit.prevent="login"
                  >
                    <v-text-field
                      v-model="email"
                      prepend-inner-icon="mdi-email-outline"
                      label="E-mail"
                      outlined
                      color="primary"
                      class="mb-3"
                      :rules="[rules.required, rules.email]"
                    />

                    <v-text-field
                      v-model="password"
                      prepend-inner-icon="mdi-lock-outline"
                      :type="showPassword ? 'text' : 'password'"
                      label="Senha"
                      outlined
                      color="primary"
                      class="mb-2"
                      :rules="[rules.required, rules.password]"
                    >
                      <template v-slot:append>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              @click="showPassword = !showPassword"
                            >
                              {{ showPassword ? "mdi-eye" : "mdi-eye-off" }}
                            </v-icon>
                          </template>
                          <span>{{
                            showPassword ? "Ocultar senha" : "Mostrar senha"
                          }}</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>

                    <div class="d-flex justify-space-between align-center mb-6">
                      <v-checkbox
                        v-model="rememberMe"
                        label="Lembrar meu acesso"
                        class="mt-0 pt-0 remember-check"
                        color="primary"
                        hide-details
                      />
                      <v-btn
                        text
                        color="primary"
                        class="px-0 forgot-btn"
                        @click="forgotPassword"
                      >
                        <span>Esqueci minha senha</span>
                      </v-btn>
                    </div>

                    <v-btn
                      type="submit"
                      color="primary"
                      rounded
                      block
                      height="50"
                      class="auth-submit-btn"
                      :loading="loading"
                      :disabled="!validForm"
                    >
                      Entrar
                    </v-btn>

                    <!-- Sessão expirada: sem isto, quem é devolvido para cá
                         por token vencido vê a tela de login do nada, sem
                         entender por que foi desconectado no meio do trabalho. -->
                    <div
                      v-if="sessaoExpirada && !errorMessage"
                      class="auth-info-msg mt-3"
                    >
                      <v-icon small color="warning" class="mr-1">
                        mdi-clock-alert-outline
                      </v-icon>
                      Sua sessão expirou por segurança. Entre novamente para
                      continuar de onde parou.
                    </div>

                    <!-- Login com Google recusado. Sem isto o usuário volta
                         para cá em silêncio e tenta de novo em looping, sem
                         saber que a recusa foi proposital. -->
                    <div
                      v-if="erroGoogle && !errorMessage"
                      class="auth-error-msg mt-3"
                    >
                      <v-icon small color="error" class="mr-1">
                        mdi-alert-circle-outline
                      </v-icon>
                      {{ erroGoogle }}
                    </div>

                    <div
                      v-if="errorMessage"
                      class="auth-error-msg mt-3"
                    >
                      <v-icon small color="error" class="mr-1">
                        mdi-alert-circle
                      </v-icon>
                      {{ errorMessage }}
                    </div>

                    <div class="auth-divider my-5">
                      <span class="auth-divider-text">Ou entre usando</span>
                    </div>

                    <div class="d-flex justify-space-between align-center">
                      <v-btn
                        rounded
                        outlined
                        class="flex-grow-1 mx-1 google-btn"
                        height="44"
                        :loading="googleLoading"
                        @click="startGoogleLogin"
                      >
                        <v-icon left color="#EA4335">mdi-google</v-icon>
                        Google
                      </v-btn>
                    </div>
                  </v-form>
                </v-col>

                <!-- LADO DIREITO -->
                <v-col
                  cols="12"
                  md="6"
                  class="auth-side-panel d-flex flex-column align-center justify-center position-relative"
                  height="100%"
                >
                  <div class="signup-overlay pa-6 text-center">
                    <v-avatar size="120" color="white" class="elevation-4 mb-4 logo-avatar">
                      <!-- <img>, e não <v-img>: o webpack só resolve "@/assets/..." em <img>.
                 No <v-img> o caminho ia cru e o avatar ficava um círculo vazio. -->
                      <img src="@/assets/salvia_symbol.svg" alt="Salvia" width="72" height="72" />
                    </v-avatar>
                    <h3 class="brand-title mb-4">Salvia</h3>
                    <h3 class="side-title mb-4">Não tem uma conta ainda?</h3>
                    <p class="side-description mb-6">
                      Cadastre-se e comece a cuidar da sua saúde de forma fácil
                      e organizada
                    </p>
                    <v-btn
                      rounded
                      outlined
                      large
                      dark
                      @click="step = 2"
                      class="px-8 side-cta"
                    >
                      <v-icon left>mdi-file-sign</v-icon>
                      CADASTRE-SE
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-window-item>

            <!-- CADASTRO -->
            <v-window-item :value="2">
              <RegisterPage @voltar="step = 1" />
            </v-window-item>
          </v-window>
        </v-card>
      </v-col>
    </v-row>

    <ForgotPasswordDialog
      v-model="showForgotPassword"
      :initial-email="email"
    />
  </v-container>
</template>

<script>
import axios from "axios";
import RegisterPage from "@/views/RegisterPage.vue";
import ForgotPasswordDialog from "@/views/ForgotPasswordDialog.vue";

export default {
  components: { RegisterPage, ForgotPasswordDialog },

  data: () => ({
    step: 1,
    showForgotPassword: false,

    // Login normal
    email: "",
    password: "",
    showPassword: false,
    rememberMe: false,
    validForm: false,
    errorMessage: "",
    loading: false,

    // Google
    googleLoading: false,

    rules: {
      required: (v) => !!v || "Este campo é obrigatório",
      email: (v) => /.+@.+\..+/.test(v) || "E-mail inválido",
      password: (v) =>
        (v && v.length >= 8) || "A senha deve ter no mínimo 8 caracteres",
    },
  }),

  computed: {
    // O interceptor do http.js manda `?expirado=1` ao devolver alguém para cá
    // por 401 (token vencido).
    sessaoExpirada() {
      return this.$route.query.expirado === "1";
    },

    // O backend manda `?google_erro=<motivo>` quando recusa vincular a conta
    // Google (GoogleAuthController::falhaDeVinculo). Motivo desconhecido cai
    // no texto genérico — nunca deixar a tela muda.
    erroGoogle() {
      const motivo = this.$route.query.google_erro;
      if (!motivo) return "";

      const mensagens = {
        conta_com_senha:
          "Já existe uma conta com este e-mail. Entre com e-mail e senha; " +
          "depois vincule o Google pelo seu perfil.",
        email_nao_verificado:
          "O Google não confirmou este e-mail. Verifique-o na sua conta " +
          "Google e tente novamente.",
        ja_vinculado:
          "Esta conta Google já está vinculada a outro usuário do sistema.",
        // A tentativa ficou velha (mais de 10 min) ou foi invalidada no meio
        // do caminho — antes isto era uma página crua de "acesso negado".
        login_expirado:
          "A tentativa de entrar com o Google expirou. Tente novamente.",
        cancelado: "O acesso pelo Google foi cancelado.",
        falha_google:
          "O Google não confirmou o acesso. Tente entrar com o Google novamente.",
      };

      return (
        mensagens[motivo] || "Não foi possível entrar com o Google."
      );
    },
  },

  watch: {
    "$route.query.step": {
      handler(newStep) {
        this.step = newStep === "2" ? 2 : 1;
      },
      immediate: true,
    },
  },

  created() {
    if (this.$route.query.step === "2") this.step = 2;
  },

  methods: {
    forgotPassword() {
      this.showForgotPassword = true;
    },

    async login() {
      if (!this.$refs.loginForm.validate()) return;

      this.loading = true;
      this.errorMessage = "";

      try {
        await this.$store.dispatch("auth/login", {
          email: this.email,
          password: this.password,
        });

        const redirect = this.$route.query.redirect;
        if (redirect) return this.$router.push(redirect);

        const userType = this.$store.getters["auth/userType"];

        if (userType === "assistant") {
          this.$router.push({
            path: "/calendar-page",
            query: { mode: "clinic" },
          });
        } else if (userType === "doctor_solo" || userType === "doctor_clinic") {
          this.$router.push({
            path: "/calendar-page",
            query: { mode: "doctor" },
          });
        } else {
          this.$router.push({ name: "PatientsPage" });
        }
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          "Falha na autenticação. Verifique suas credenciais.";
      } finally {
        this.loading = false;
      }
    },

    // ====== GOOGLE LOGIN (novo fluxo: direto) ======
    async startGoogleLogin() {
      this.googleLoading = true;
      this.errorMessage = "";

      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/auth/google/login/redirect`,
        );

        window.location.href = data.redirect_url;
      } catch (err) {
        this.errorMessage =
          err.response?.data?.message ||
          "Não foi possível iniciar o login com Google. Tente novamente.";
      } finally {
        this.googleLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-container {
  background: radial-gradient(
    1200px circle at 20% 10%,
    var(--ds-blue-100) 0%,
    var(--ds-surface-white) 45%,
    var(--ds-blue-bg) 100%
  );
}

.auth-shell {
  border-radius: 18px !important;
  box-shadow: 0 16px 40px rgba(31, 77, 63, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.04) !important;
}

.auth-section-header {
  display: flex;
  align-items: center;
  padding-bottom: 6px;
}

.auth-section-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--ds-primary);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.auth-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.5px;
}

.auth-subtitle {
  font-size: 14px;
  color: var(--ds-text-soft);
  font-weight: 500;
  margin: 0;
}

.remember-check :deep(.v-label) {
  font-size: 13px;
  color: var(--ds-text-550);
}

.forgot-btn {
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
}

.auth-submit-btn {
  font-size: 14px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
  text-transform: none !important;
  color: var(--ds-white) !important;
  box-shadow: 0 6px 16px rgba(31, 77, 63, 0.28) !important;
}

.auth-error-msg {
  font-size: 13px;
  color: var(--ds-danger-mid);
  text-align: center;
  background: #ffebee;
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Aviso de sessão expirada — informativo, não é erro do usuário. */
.auth-info-msg {
  font-size: 13px;
  color: #8a5a10;
  text-align: center;
  background: #fdf6e3;
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.4;
}

.auth-divider {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-divider::before,
.auth-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--ds-gray-300);
}

.auth-divider-text {
  font-size: 11px;
  font-weight: 600;
  color: var(--ds-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 0 16px;
}

.google-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  border: 1.5px solid var(--ds-gray-300) !important;
  color: var(--ds-text-600) !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.google-btn:hover {
  border-color: var(--ds-primary) !important;
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.1) !important;
}

/* Side panel */
.auth-side-panel {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-dark) 60%, var(--ds-primary-darker) 100%);
  position: relative;
  overflow: hidden;
}

.auth-side-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 75%);
  pointer-events: none;
}

.signup-overlay {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
}

.logo-avatar {
  background: var(--ds-surface-white) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18) !important;
  border: 4px solid rgba(255, 255, 255, 0.15);
}

.brand-title {
  font-size: 32px;
  font-weight: 800;
  color: var(--ds-white);
  letter-spacing: -0.8px;
}

.side-title {
  font-size: 22px;
  font-weight: 600;
  color: var(--ds-white);
  letter-spacing: -0.3px;
}

.side-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  max-width: 320px;
  margin: 0 auto;
}

.side-cta {
  font-size: 13px !important;
  font-weight: 700 !important;
  letter-spacing: 0.4px !important;
  border: 2px solid rgba(255, 255, 255, 0.4) !important;
  height: 46px !important;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.side-cta:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  border-color: var(--ds-white) !important;
}

/* Aumentar altura mínima do card em telas menores */
@media (max-width: 960px) {
  .v-card {
    min-height: 650px;
  }
}

/* Animação suave para troca de telas */
.v-window-item {
  transition: all 0.3s ease-in-out;
}

.logo-avatar {
  transition: transform 0.3s ease;
}

.logo-avatar:hover {
  transform: scale(1.05);
}
</style>
